import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import sessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Simple OTP storage for development
  const otpStorage = new Map();

  async function checkEmailExists(email) {
    try {
      console.log('Checking if email exists in Firestore:', email);
      const usersQuery = query(collection(db, 'users'), where('email', '==', email));
      const usersSnapshot = await getDocs(usersQuery);
      
      if (!usersSnapshot.empty) {
        console.log('Email already exists in Firestore');
        return true;
      }
      
      // Also check tutors collection
      const tutorsQuery = query(collection(db, 'tutors'), where('email', '==', email));
      const tutorsSnapshot = await getDocs(tutorsQuery);
      
      if (!tutorsSnapshot.empty) {
        console.log('Email already exists in tutors collection');
        return true;
      }
      
      console.log('Email is available');
      return false;
    } catch (error) {
      console.error('Error checking email existence:', error);
      return false; // If error, assume email is available
    }
  }

  function signup(email, password, name, phone, educationData = null, countryCode = 'in') {
    console.log('Starting signup process for:', email);
    console.log('Current auth state:', auth.currentUser);
    console.log('Education data:', educationData);
    
    // Check if user is already logged in
    if (auth.currentUser) {
      console.log('User already logged in:', auth.currentUser.email);
      if (auth.currentUser.email === email) {
        console.log('Same email, user already exists');
        throw new Error('This email is already registered. Please use a different email or try logging in.');
      }
      // Sign out current user first
      return signOut(auth).then(() => {
        console.log('Signed out existing user, now creating new account');
        return createUserWithEmailAndPassword(auth, email, password);
      });
    }
    
    // Check if email already exists in Firestore
    return checkEmailExists(email).then((emailExists) => {
      if (emailExists) {
        throw new Error('This email is already registered. Please use a different email or try logging in.');
      }
      
      return createUserWithEmailAndPassword(auth, email, password);
    })
      .then(async (userCredential) => {
        const user = userCredential.user;
        console.log('User created with UID:', user.uid);
        console.log('User email:', user.email);
        
        try {
          // Update profile with display name
          await updateProfile(user, {
            displayName: name
          });
          console.log('Profile updated successfully');

          // Send email verification
          await sendEmailVerification(user);
          console.log('Email verification sent');

          // Determine currency based on country code
          const currency = countryCode === 'in' ? 'INR' : 'USD';
          
          // Save user data to Firestore
          const userData = {
            name: name,
            email: email,
            phone: phone,
            countryCode: countryCode,
            currency: currency,
            createdAt: new Date(),
            bookings: [],
            emailVerified: false,
            ...(educationData && {
              education: {
                educationLevel: educationData.educationLevel || '',
                class: educationData.class || '',
                board: educationData.board || '',
                subjects: educationData.subjects || [],
                examPreparation: educationData.examPreparation || [],
                additionalNotes: educationData.additionalNotes || ''
              }
            })
          };
          
          console.log('Saving user data to Firestore:', userData);
          await setDoc(doc(db, 'users', user.uid), userData);
          console.log('User data saved to Firestore successfully');

          // Create session for the new user
          const sessionData = {
            uid: user.uid,
            email: user.email,
            displayName: name
          };
          
          console.log('Creating user session:', sessionData);
          sessionManager.createUserSession(user.uid, sessionData);
          console.log('User session created successfully');

          return userCredential;
        } catch (error) {
          console.error('Error in signup process:', error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          throw error;
        }
      })
      .catch((error) => {
        console.error('Firebase signup error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        throw error;
      });
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        const user = userCredential.user;
        
        // Create user session using session manager
        const userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0]
        };
        
        sessionManager.createUserSession(user.uid, userData);
        
        return userCredential;
      });
  }

  function logout() {
    // Clear session before Firebase logout
    sessionManager.logoutCurrentUser();
    return signOut(auth);
  }

  function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider)
      .then(async (result) => {
        const user = result.user;
        
        // Check if user already exists in Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (!userDoc.exists()) {
          // Save new Google user data to Firestore
          await setDoc(doc(db, 'users', user.uid), {
            name: user.displayName || 'Google User',
            email: user.email,
            phone: user.phoneNumber || '',
            createdAt: new Date(),
            bookings: [],
            emailVerified: true // Google users are pre-verified
          });
        }
        
        // Create user session using session manager
        const userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0]
        };
        
        sessionManager.createUserSession(user.uid, userData);
        
        return result;
      });
  }

  // Send OTP using backend API
  async function sendOTP(email) {
    try {
      // Normalize email: lowercase and trim
      const normalizedEmail = email.trim().toLowerCase();
      
      const response = await fetch(`${API_BASE_URL}/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to send OTP');
      }

      const data = await response.json();
      console.log('📧 OTP response:', data);
      
      if (data.success) {
        console.log(`✅ OTP sent successfully to ${email}`);
        if (data.otp) {
          console.log(`🔑 OTP Code: ${data.otp} (for development/testing)`);
        }
        console.log('🔍 Check your email inbox for the OTP');
        
        return { 
          success: true, 
          message: data.message || `OTP sent to ${email}`,
          otp: data.otp || null // OTP only available in development mode
        };
      } else {
        throw new Error(data.error || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      
      // Check if response is HTML (404 page) instead of JSON
      if (error.message.includes('Unexpected token') || error.message.includes('<!doctype') || error.message.includes('<!DOCTYPE')) {
        throw new Error('Backend server is not available. Please contact support or try again later.');
      }
      
      // Provide more specific error messages
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('ECONNREFUSED')) {
        // Check if we're in production
        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
          throw new Error('Cannot connect to server. Please contact support.');
        } else {
          throw new Error('Cannot connect to OTP server. Please make sure the backend server is running on port 5001. Run: npm run server');
        }
      }
      
      throw new Error(error.message || 'Failed to send OTP. Please try again.');
    }
  }

  // Verify OTP using backend API
  async function verifyOTP(email, otp) {
    try {
      // Normalize email and OTP: lowercase and trim
      const normalizedEmail = email.trim().toLowerCase();
      const normalizedOtp = otp.toString().trim();
      
      const response = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail, otp: normalizedOtp }),
      });

      // Check if response is HTML (404 page) instead of JSON
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('application/json')) {
        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
          throw new Error('Backend server is not available. Please contact support.');
        } else {
          throw new Error('Cannot connect to OTP server. Please make sure the backend server is running.');
        }
      }
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify OTP');
      }
      
      return { success: true, message: 'OTP verified successfully' };
    } catch (error) {
      console.error('Error verifying OTP:', error);
      
      // Check if error is due to HTML response
      if (error.message.includes('Unexpected token') || error.message.includes('<!doctype') || error.message.includes('<!DOCTYPE')) {
        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
          throw new Error('Backend server is not available. Please contact support.');
        } else {
          throw new Error('Cannot connect to OTP server. Please make sure the backend server is running.');
        }
      }
      
      throw new Error(error.message || 'Failed to verify OTP');
    }
  }

  async function getUserData(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        setCurrentUser(user);
        
        // Check if we have a valid session
        if (!sessionManager.getCurrentSession()) {
          // Create session if none exists
          const userData = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email.split('@')[0]
          };
          sessionManager.createUserSession(user.uid, userData);
        }
      } else {
        // User is signed out
        setCurrentUser(null);
        // Clear session when user signs out
        sessionManager.logoutCurrentUser();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  async function resetPassword(email) {
    try {
      // Use our custom email service instead of Firebase's default
      const response = await fetch(`${API_BASE_URL}/send-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send password reset email');
      }
      
      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  const value = {
    currentUser,
    signup,
    login,
    logout,
    signInWithGoogle,
    sendOTP,
    verifyOTP,
    getUserData,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
